/**
 * Widgets registry — declarative mounting for `{kind: 'widget'}` blocks
 * in article content.
 *
 * Authors declare a widget in `PagePlan.interactive[]`:
 *   { id: 'm', kind: 'month-strip', anchor: 'climate-year',
 *     facts: ['some-fact-id-if-needed'],
 *     mobile: 'horizontal-scroll',
 *     props: { data: [...12...], caption: '…' } }
 *
 * They reference it from prose via a ContentBlock:
 *   { kind: 'widget', widgetId: 'm' }
 *
 * The renderer (`Renderer.tsx`) calls `renderWidget()` here, which:
 *   1. Looks up the WidgetPlan by id
 *   2. Picks the React component by kind
 *   3. Resolves any declared facts from the registry
 *   4. Mounts the component with the merged props
 *
 * Adding a new widget kind:
 *   1. Add it to `WidgetKind` in `page-plan.ts`.
 *   2. Add the component file under `widgets/`.
 *   3. Wire it into the switch below.
 *   4. Document the new kind in `INTERACTIVE.md`.
 */

import type { LangCode } from '../architecture';
import type { WidgetPlan } from '../page-plan';
import type { FactsRegistry, ResolvedFact } from '../facts';

import { MonthStrip, type MonthData } from './MonthStrip';
import { ComparisonBar, type ComparisonRow } from './ComparisonBar';
import { FactTable } from './FactTable';
import { StatCard } from './StatCard';

interface RenderArgs {
  plan: WidgetPlan;
  registry: FactsRegistry;
  lang: LangCode;
}

export function renderWidget({ plan, registry, lang }: RenderArgs): React.ReactNode {
  // Resolve declared facts into a positional map so widgets can pick by id.
  const resolved: Record<string, ResolvedFact> = {};
  for (const id of plan.facts) {
    const f = registry.resolve(id);
    if (f) resolved[id] = f;
  }

  const props = plan.props ?? {};

  switch (plan.kind) {
    case 'annual-timeline':
      return (
        <MonthStrip
          lang={lang}
          data={(props.data as MonthData[]) ?? []}
          caption={typeof props.caption === 'string' ? props.caption : undefined}
          tempRange={Array.isArray(props.tempRange) ? (props.tempRange as [number, number]) : undefined}
        />
      );

    case 'comparison-bar':
      return (
        <ComparisonBar
          lang={lang}
          rows={(props.rows as ComparisonRow[]) ?? []}
          caption={typeof props.caption === 'string' ? props.caption : undefined}
          unit={typeof props.unit === 'string' ? props.unit : ''}
          range={Array.isArray(props.range) ? (props.range as [number, number]) : undefined}
          decimals={typeof props.decimals === 'number' ? props.decimals : 0}
        />
      );

    case 'fact-table':
      return (
        <FactTable
          lang={lang}
          rows={(props.rows as { label: string; note?: string; factId: string }[]) ?? []}
          facts={resolved}
          caption={typeof props.caption === 'string' ? props.caption : undefined}
        />
      );

    case 'stat-card':
      return (
        <StatCard
          lang={lang}
          eyebrow={String(props.eyebrow ?? '')}
          value={String(props.value ?? '—')}
          helper={typeof props.helper === 'string' ? props.helper : undefined}
        />
      );

    case 'mini-map':
    case 'scrollytelling':
    case 'radar':
    default:
      // Not yet implemented — render a flagged placeholder so authors see
      // it in dev and the bundle doesn't crash.
      return (
        <div
          aria-hidden="true"
          className="my-6 rounded-lg border border-dashed border-ink-100 bg-paper-2/60 px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-ink-400"
        >
          widget · {plan.kind} · {plan.id}
        </div>
      );
  }
}

export type { MonthData, ComparisonRow };
